import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { scrollToId } from "../hooks/useLenis";

const PLANS = [
  {
    name: "Découverte",
    price: "19€",
    unit: "/ pers · 30 min",
    machine: "katvr",
    duration: 30,
    features: [
      "Tapis KAT VR Walk Mini",
      "Initiation accompagnée",
      "1 expérience au choix",
      "Casque dernière génération",
    ],
  },
  {
    name: "Session Hardcore",
    price: "32€",
    unit: "/ pers · 60 min",
    machine: "katvr",
    duration: 60,
    tag: "Populaire",
    features: [
      "Tapis KAT VR Walk Mini",
      "2 expériences au choix",
      "Mode multi-joueurs",
      "Classement en salle",
    ],
  },
  {
    name: "Chaise 360°",
    price: "5€",
    unit: "/ séance · 20 min",
    machine: "chair360",
    duration: 20,
    features: [
      "Rotation gyroscopique 360°",
      "Sensations extrêmes",
      "Simulations spatiales",
      "Vibrations haptiques",
    ],
  },
  {
    name: "Privatisation",
    price: "150€",
    unit: "/ heure · salle complète",
    private: true,
    features: [
      "5 tapis KAT VR + chaise 360°",
      "Jusqu'à 12 joueurs",
      "Animateur dédié",
      "Anniversaires & team-building",
    ],
  },
];

export const PricingSection = ({ onReserve }) => (
  <section id="tarifs" data-testid="pricing-section" className="relative py-24 sm:py-28">
    <div className="mx-auto max-w-7xl px-5">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
      >
        <p className="font-hud text-xs uppercase tracking-[0.35em] text-cyan-400">
          {"// 03 — Tarifs"}
        </p>
        <h2 className="mt-4 font-display text-3xl font-extrabold uppercase tracking-tight text-slate-100 sm:text-4xl lg:text-5xl">
          Sessions & <span className="text-glow-cyan text-cyan-400">formules</span>
        </h2>
      </motion.div>

      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {PLANS.map((plan, i) => (
          <motion.div
            key={plan.name}
            data-testid={`pricing-card-${plan.name.toLowerCase().replace(/\s/g, "-")}`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ delay: i * 0.1, duration: 0.6 }}
            className={`relative flex flex-col border bg-surface p-7 transition-all duration-300 ${
              plan.tag
                ? "border-fuchsia-500/60 shadow-[0_0_36px_rgba(255,0,122,0.15)] hover:shadow-[0_0_48px_rgba(255,0,122,0.25)]"
                : "border-line hover:border-cyan-400/50 hover:shadow-[0_0_32px_rgba(0,240,255,0.1)]"
            }`}
          >
            {plan.tag && (
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-fuchsia-500 px-3 py-1 font-hud text-[10px] font-bold uppercase tracking-[0.2em] text-white">
                {plan.tag}
              </span>
            )}
            <h3 className="font-hud text-xs uppercase tracking-[0.25em] text-slate-400">
              {plan.name}
            </h3>
            <div className="mt-4 flex items-baseline gap-2">
              <span
                className={`font-orbitron text-4xl font-black ${
                  plan.tag ? "text-fuchsia-400" : "text-cyan-400"
                }`}
              >
                {plan.price}
              </span>
              <span className="font-hud text-[10px] uppercase tracking-wider text-slate-500">
                {plan.unit}
              </span>
            </div>
            <ul className="mt-6 flex-1 space-y-3 border-t border-line pt-6">
              {plan.features.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
                  <Check
                    className={`mt-0.5 h-4 w-4 shrink-0 ${
                      plan.tag ? "text-fuchsia-400" : "text-cyan-400"
                    }`}
                  />
                  {f}
                </li>
              ))}
            </ul>
            <button
              data-testid={`pricing-reserve-${plan.name.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() =>
                plan.private
                  ? scrollToId("#infos")
                  : onReserve({ machine: plan.machine, duration: plan.duration })
              }
              className={`mt-8 w-full py-3 font-hud text-xs font-bold uppercase tracking-[0.2em] transition-all duration-300 ${
                plan.tag
                  ? "bg-fuchsia-500 text-white hover:bg-fuchsia-400 hover:shadow-[0_0_28px_rgba(255,0,122,0.5)]"
                  : "border border-cyan-500/40 text-cyan-300 hover:bg-cyan-400 hover:text-void hover:shadow-[0_0_28px_rgba(0,240,255,0.4)]"
              }`}
            >
              {plan.private ? "Nous contacter" : "Réserver"}
            </button>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
